import cornerstoneTools from "cornerstone-tools";
import Drawing from "../api/Drawing";
import Util from "../api/Util";
import {
  ANNOTATION_TEXT_X_OFFSET_PX,
  MEASUREMENT_SUFFIXES,
  TOOL_IDS,
} from "../consts/tools.consts";
import {
  getAxisLengths,
  getDimensionData,
  getDisplayedMeasurement,
  getTextBoxAnchorPoints,
} from "./measurementToolUtils";
import { DEFAULT_HANDLE } from "../consts/tools.defaults";
import { ExtendedAnnotationTool } from "../api/ExtendedAnnotationTool";
import { ToolData } from "../tools.types";

const LENGTH_TOOL_CONFIGURATION = {
  drawHandles: true,
  drawHandlesOnHover: false,
  hideHandlesIfMoving: false,
  renderDashed: true,
  digits: 2,
};
export default class LengthTool extends ExtendedAnnotationTool {
  constructor() {
    super(TOOL_IDS.LENGTH_TOOL);
  }

  createNewMeasurement = (eventData) => {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;
    if (!goodEventData) {
      return;
    }

    const { x, y } = eventData.currentPoints.image;

    return {
      visible: true,
      active: true,
      color: undefined,
      invalidated: true,
      handles: {
        start: {
          x,
          y,
          highlight: true,
          active: false,
        },
        end: {
          x,
          y,
          highlight: true,
          active: true,
        },
        textBox: {
          active: false,
          hasMoved: false,
          movesIndependently: false,
          drawnIndependently: true,
          allowedOutsideImage: true,
          hasBoundingBox: true,
        },
      },
    };
  };

  pointNearTool = (element, data, coords) => {
    const hasStartAndEndHandles =
      data && data.handles && data.handles.start && data.handles.end;
    const validParameters = hasStartAndEndHandles;

    if (!validParameters) {
      return false;
    }

    if (data.visible === false) {
      return false;
    }

    return (
      Util.lineSegDistance(
        element,
        data.handles.start,
        data.handles.end,
        coords
      ) < 25
    );
  };

  updateCachedStats(image, _element, data) {
    const {
      handles: { end, start },
    } = data;
    const { rowPixelSpacing, colPixelSpacing } = getDimensionData(
      image,
      this.imageMetaData
    );
    const { dx, dy } = getAxisLengths(
      start,
      end,
      colPixelSpacing,
      rowPixelSpacing
    );
    const length = Math.sqrt(dx * dx + dy * dy);
    data.suffix = MEASUREMENT_SUFFIXES.MM;
    data.value = length;
    data.invalidated = false;
  }

  drawToolData(
    element: HTMLElement,
    context: CanvasRenderingContext2D,
    toolData: ToolData
  ) {
    const {
      value,
      suffix,
      visible,
      handles: { start, end, textBox },
    } = toolData;
    const { drawHandlesOnHover, hideHandlesIfMoving, drawHandles } =
      LENGTH_TOOL_CONFIGURATION;
    const color = cornerstoneTools.toolColors.getColorIfActive(toolData);
    if (visible !== false) {
      const lineOptions = { color };
      Drawing.setShadow(context, LENGTH_TOOL_CONFIGURATION);
      Drawing.drawLine(context, element, start, end, lineOptions);
      const handleOptions = {
        ...DEFAULT_HANDLE,
        color,
        drawHandlesIfActive: drawHandlesOnHover,
        hideHandlesIfMoving,
      };
      if (drawHandles) {
        Drawing.drawHandles(
          context,
          { element },
          { start, end },
          handleOptions
        );
      }

      if (!textBox.hasMoved) {
        const coords = {
          x: Math.max(start.x, end.x),
          y: end.y,
        };
        // Depending on which handle has the largest x-value,
        // Set the y-value for the text box
        if (coords.x === start.x) {
          coords.y = start.y;
        }

        textBox.x = coords.x;
        textBox.y = coords.y;
      }

      const text = getDisplayedMeasurement(value, suffix);
      const lineWidth = 3;
      Drawing.drawLinkedTextBox(
        context,
        element,
        textBox,
        text,
        { start, end, textBox },
        getTextBoxAnchorPoints,
        color,
        lineWidth,
        ANNOTATION_TEXT_X_OFFSET_PX,
        true
      );
    }
  }
}
